import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useState } from 'react';
import Input from '../../../form/Input';
import Select from '../../../form/Select';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import DatePickerInput from '../../../form/DatePickerInput';
import { formatDate } from '../../../../util/util';
import MobileFilterTabs from '../../../filter/MobileFilterTabs';

interface IFilter {
  telRef: React.RefObject<HTMLInputElement>;
  emailRef: React.RefObject<HTMLInputElement>;
  nameRef: React.RefObject<HTMLInputElement>;
  getFilteredData: (params: any) => void; // API 호출 함수
}

const OrderListFilter = ({
  telRef,
  emailRef,
  nameRef,
  getFilteredData,
}: IFilter) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [realtimeYn, setRealtimeYn] = useState<string>('N');
  const [pageSize, setPageSize] = useState<string>('20');
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const currentQuery = queryString.parse(location.search);
    if (currentQuery.contractStartDt) {
      return new Date(currentQuery.contractStartDt as string);
    }
    const monthAgo = new Date();
    monthAgo.setDate(monthAgo.getDate() - 30);
    return monthAgo;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const currentQuery = queryString.parse(location.search);
    if (currentQuery.contractEndDt) {
      return new Date(currentQuery.contractEndDt as string);
    }
    return new Date();
  });

  useEffect(() => {
    const currentQuery = queryString.parse(location.search);

    // 쿼리스트링 값에 따라 필드 업데이트
    if (currentQuery.realtimeYn) {
      setRealtimeYn(currentQuery.realtimeYn as string);
    } else {
      setRealtimeYn('N'); // 기본값
    }

    if (currentQuery.size) {
      setPageSize(currentQuery.size as string);
    } else {
      setPageSize('20'); // 기본값
    }

    if (currentQuery.telNo && telRef.current) {
      telRef.current.value = currentQuery.telNo as string;
    } else if (telRef.current) {
      telRef.current.value = ''; // 초기화
    }

    if (currentQuery.email && emailRef.current) {
      emailRef.current.value = currentQuery.email as string;
    } else if (emailRef.current) {
      emailRef.current.value = ''; // 초기화
    }

    if (currentQuery.custName && nameRef.current) {
      nameRef.current.value = currentQuery.custName as string;
    } else if (nameRef.current) {
      nameRef.current.value = ''; // 초기화
    }

    if (currentQuery.contractStartDt) {
      setStartDate(new Date(currentQuery.contractStartDt as string));
    } else {
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      setStartDate(monthAgo);
    }

    if (currentQuery.contractEndDt) {
      setEndDate(new Date(currentQuery.contractEndDt as string));
    } else {
      setEndDate(new Date());
    }
  }, [location.search, telRef, emailRef, nameRef]);

  const updateQueryString = useCallback(
    (updatedFields: Record<string, string | undefined>) => {
      const currentQuery = queryString.parse(location.search);
      const updatedQuery = {
        ...currentQuery,
        ...updatedFields,
      };

      const newQueryString = queryString.stringify(updatedQuery);
      navigate(`?${newQueryString}`);
    },
    [location.search, navigate]
  );

  const onChangeRealtime = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value as 'Y' | 'N';
      setRealtimeYn(value);
      updateQueryString({ realtimeYn: value, page: '0' });
    },
    [updateQueryString]
  );

  const onChangePageSize = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      setPageSize(value);
      updateQueryString({ size: value, page: '0' });
    },
    [updateQueryString]
  );

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      setStartDate(date);
      updateQueryString({
        contractStartDt: date ? formatDate(date)['yyyy-mm-ddTHH:mm:ss'] : undefined,
      });
    },
    [updateQueryString]
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      setEndDate(date);
      updateQueryString({
        contractEndDt: date ? formatDate(date)['yyyy-mm-ddTHH:mm:ss'] : undefined,
      });
    },
    [updateQueryString]
  );

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const params = {
        realtimeYn,
        size: pageSize,
        telNo: telRef.current?.value || undefined,
        email: emailRef.current?.value || undefined,
        custName: nameRef.current?.value || undefined,
        contractStartDt: startDate
          ? formatDate(startDate)['yyyy-mm-ddTHH:mm:ss']
          : undefined,
        contractEndDt: endDate
          ? formatDate(endDate)['yyyy-mm-ddTHH:mm:ss']
          : undefined,
      };

      getFilteredData(params);
    },
    [getFilteredData, realtimeYn, pageSize, startDate, endDate, telRef, emailRef, nameRef]
  );

  const realtimeOptions = [
    { option: '실시간 X', value: 'N' },
    { option: '실시간', value: 'Y' },
  ];

  const sizeOptions = [
    { option: '20개씩', value: '20' },
    { option: '50개씩', value: '50' },
    { option: '100개씩', value: '100' },
    { option: '300개씩', value: '300' },
    { option: '500개씩', value: '500' },
  ];

  // 모바일 검색 폼 콘텐츠
  const mobileSearchContent = (
    <form onSubmit={handleSearch} className="!p-3 !space-y-2">
      {/* 1행: 실시간 + 페이지 사이즈 */}
      <div className="!grid !grid-cols-2 !gap-2">
        <Select
          options={realtimeOptions}
          onChange={onChangeRealtime}
          value={realtimeYn}
          style={{ width: '100%' }}
        />
        <Select
          options={sizeOptions}
          onChange={onChangePageSize}
          value={pageSize}
          style={{ width: '100%' }}
        />
      </div>

      {/* 2행: 날짜 */}
      <div className="!flex !items-center !gap-1">
        <div className="!flex-1">
          <DatePickerInput
            value={startDate}
            onChange={handleStartDateChange}
            placeholder="시작"
            width="100%"
          />
        </div>
        <span className="!text-gray-400 !text-xs">~</span>
        <div className="!flex-1">
          <DatePickerInput
            value={endDate}
            onChange={handleEndDateChange}
            placeholder="종료"
            width="100%"
          />
        </div>
      </div>

      {/* 3행: 검색어 입력 */}
      <div className="!flex !gap-1">
        <Input inputRef={telRef} placeholder="전화번호" textAlign="left" width="38%" />
        <Input inputRef={emailRef} placeholder="이메일" textAlign="left" width="42%" />
        <Input inputRef={nameRef} placeholder="이름" textAlign="left" width="20%" />
      </div>

      {/* 하단: 검색 버튼 */}
      <button
        type="submit"
        className="!w-full !py-2 !text-sm !text-white !bg-blue-500 !rounded hover:!bg-blue-600"
      >
        검색
      </button>
    </form>
  );

  return (
    <div className="!w-full">
      {/* 모바일: MobileFilterTabs 사용 */}
      <MobileFilterTabs
        tabs={[{ id: 'search', label: '검색', content: mobileSearchContent }]}
      />

      {/* 데스크톱 검색 필터 */}
      <form
        onSubmit={handleSearch}
        className="!hidden md:!flex !flex-row !items-center !justify-between !w-full !gap-4"
      >
        <div className="!flex !items-center !gap-3 !flex-wrap">
          <div className="!flex !items-center !gap-1">
            <DatePickerInput
              value={startDate}
              onChange={handleStartDateChange}
              placeholder="시작일"
              width={130}
            />
            <span className="!text-gray-400">~</span>
            <DatePickerInput
              value={endDate}
              onChange={handleEndDateChange}
              placeholder="종료일"
              width={130}
            />
          </div>
          <Select
            options={realtimeOptions}
            onChange={onChangeRealtime}
            value={realtimeYn}
            style={{ width: '120px' }}
          />
          <Input
            inputRef={telRef}
            textAlign="left"
            width={140}
            placeholder="전화번호"
          />
          <Input
            inputRef={emailRef}
            textAlign="left"
            width={200}
            placeholder="이메일"
          />
          <div className="!relative">
            <Input
              inputRef={nameRef}
              textAlign="left"
              width={160}
              placeholder="이름"
              style={{ paddingRight: '40px' }}
            />
            <button
              type="submit"
              className="!absolute !right-1 !top-1/2 !-translate-y-1/2 !w-8 !h-8 !bg-blue-400 hover:!bg-blue-500 !rounded !flex !items-center !justify-center !transition-colors !border-0"
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} className="!text-white !text-sm" />
            </button>
          </div>
        </div>
        <Select
          options={sizeOptions}
          onChange={onChangePageSize}
          value={pageSize}
          style={{ width: '120px' }}
        />
      </form>
    </div>
  );
};

export default OrderListFilter;
